import { useEffect, useState } from "react";

/**
 * Native HTML5 drag-and-drop reordering for a grid/list of admin cards —
 * no drag-and-drop library installed anywhere in this app, and pulling
 * one in just for five "Manage X" grids (courses, ferries, transport,
 * accommodation, getaways) isn't worth it when the browser already does
 * this natively.
 *
 * Reordering is optimistic: dropping updates the displayed order
 * immediately and fires `onPersist(orderedIds)` in the background (pass
 * e.g. `(ids) => saveItemOrder("course", ids)`); if that rejects, the
 * list reverts to the last order the parent actually gave it.
 *
 * @template T
 * @param {T[]} items - current items, in their last known-good order
 * @param {(item: T) => string} getId
 * @param {(orderedIds: string[]) => Promise<void>} onPersist
 */
export function useDragReorder(items, getId, onPersist) {
  const [localItems, setLocalItems] = useState(items);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Resync whenever the parent's list changes for any reason that isn't
  // our own drag (a fresh fetch, a create, a delete) — not on every
  // render, so a drag-in-progress doesn't get clobbered by a stray
  // re-render with the same array contents in a new reference.
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const handleDragStart = (item) => (e) => {
    setDragId(getId(item));
    // Firefox requires dataTransfer to be touched for drag to start at all.
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", getId(item));
  };

  const handleDragOver = (item) => (e) => {
    e.preventDefault();
    const id = getId(item);
    if (id !== overId) setOverId(id);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setOverId(null);
  };

  const handleDrop = (item) => (e) => {
    e.preventDefault();
    const targetId = getId(item);
    const draggedId = dragId;
    setDragId(null);
    setOverId(null);
    if (draggedId == null || draggedId === targetId) return;

    const fromIndex = localItems.findIndex((it) => getId(it) === draggedId);
    const toIndex = localItems.findIndex((it) => getId(it) === targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const next = [...localItems];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setLocalItems(next);

    onPersist(next.map(getId)).catch((err) => {
      console.error("Failed to save new order:", err);
      setLocalItems(items); // revert to the parent's last known-good order
    });
  };

  const getDragHandlers = (item) => ({
    draggable: true,
    onDragStart: handleDragStart(item),
    onDragOver: handleDragOver(item),
    onDrop: handleDrop(item),
    onDragEnd: handleDragEnd,
  });

  return {
    items: localItems,
    getDragHandlers,
    isDragging: (item) => dragId === getId(item),
    isDragOver: (item) => overId === getId(item) && dragId !== getId(item),
  };
}
